import { useCallback, useSyncExternalStore } from "react";

/**
 * `true` while the given media query matches, re-rendering when it changes.
 *
 * Extracted from `WorkspaceRightRail`, which owned a private copy, so the
 * workspace control group can decide its collapse stage against the SAME
 * breakpoints the rail uses. Two components disagreeing about where "tablet"
 * starts is exactly the drift a shared hook prevents.
 *
 * SSR/jsdom-safe: returns `false` when `matchMedia` is unavailable, which is
 * also the server snapshot — a layout that must not assume a viewport it cannot
 * measure. Note `useReducedMotion` stays separate on purpose: it hard-codes one
 * query and reads as an intent rather than a measurement.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (notify: () => void): (() => void) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const media = window.matchMedia(query);
      media.addEventListener("change", notify);
      return () => media.removeEventListener("change", notify);
    },
    [query],
  );
  const getSnapshot = useCallback((): boolean => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
